import { useCallback, useState, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import {
  PIShell,
  Drill,
  PIInsight,
  PINoteImage,
  PracticeLadder,
} from "@/components/pi/PIShell";
import { PianoDiagram, KeyHighlight } from "@/components/PianoDiagram";
import { LessonMidi } from "@/components/MidiPlayer";
import { Notation } from "@/components/Notation";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import {
  TWINKLE_MELODY,
  flatten,
  melodyToMeasures,
  melodyToSequence,
  midiToPitchName,
  tonicMidiForPc,
} from "@/theory/melody";
import { kickAudio, playSequence } from "@/audio/piano";

/**
 * Piano Intermediate · Lesson 4 — Twinkle Twinkle, Left Hand Arpeggios.
 *
 * Same melody as Lesson 3, now with a flowing arpeggio underneath instead
 * of single bass notes. The pattern is L-H-M-H (low–high–middle–high),
 * Jason's chart: tonic C-G-E-G (1-3-5), predominant C-A-F-A (1-4-6),
 * dominant B-G-F-G (7-4-5, a G7 without its D).
 */

/** L-H-M-H shapes, semitones above the tonic (low, high, middle, high). */
const SHAPES: Record<"T" | "P" | "D", number[]> = {
  T: [0, 7, 4, 7],   // 1-5-3-5
  P: [0, 9, 5, 9],   // 1-6-4-6
  D: [-1, 7, 5, 7],  // 7-5-4-5
};

/** Jason's "Chords for Twinkle" chart — one chord per half bar, six lines. */
const CHORD_CHART: ("T" | "P" | "D")[][] = [
  ["T", "T", "P", "T"],
  ["D", "T", "D", "T"],
  ["T", "P", "T", "D"],
  ["T", "P", "T", "D"],
  ["T", "T", "P", "T"],
  ["D", "T", "D", "T"],
];

export default function PianoIntermediate4() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "4");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-intermediate"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyProvider defaultTonic="C" availableQualities={["major"]}>
        <PIShell
          title="Twinkle Twinkle — Left Hand Arpeggios"
          exerciseGoal="A flowing left-hand arpeggio under the same melody you already know — Jason\'s go-to L-H-M-H pattern."
          bpm={84}
          difficulty="Intermediate"
        >
          <Body />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

function Body() {
  const k = useKeyOrDefault();
  const tonicMidi = tonicMidiForPc(k.tonicPc);

  const [activePcs, setActivePcs] = useState<Set<number>>(new Set());
  const onMidiNote = useCallback((p: number) => {
    setActivePcs((prev) => {
      const next = new Set(prev);
      next.add(p);
      setTimeout(() => {
        setActivePcs((cur) => {
          const n = new Set(cur);
          n.delete(p);
          return n;
        });
      }, 300);
      return next;
    });
  }, []);

  // Master keyboard: I (gold) + the L-H-H tones for it
  const litHighlights: KeyHighlight[] = [
    { degree: 1, tone: "root",  label: "1" },
    { degree: 3, tone: "voice", label: "3" },
    { degree: 5, tone: "voice", label: "5" },
  ];

  // Build a continuous LH arpeggio sequence at the bass register.
  // Jason's chart: L-H-M-H, four eighth notes per half bar. Each chord is a
  // fixed shape in scale degrees (semitones above the tonic):
  //   T  tonic        1-3-5 → C-G-E-G
  //   P  predominant  1-4-6 → C-A-F-A
  //   D  dominant     7-4-5 → B-G-F-G (G7 without its D)
  const playArpeggio = async () => {
    kickAudio();
    const BPM = 84;
    const eighth = 60 / BPM / 2;
    const bassTonic = tonicMidi - 12;
    const events: { time: number; notes: string[]; duration: string }[] = [];
    let t = 0;
    for (const slot of CHORD_CHART.flat()) {
      for (const off of SHAPES[slot]) {
        events.push({
          time: t,
          notes: [midiToPitchName(bassTonic + off, k.useFlats)],
          duration: "8n",
        });
        t += eighth;
      }
    }
    await playSequence(events, BPM);
  };

  return (
    <>
      <StudyDeck>
        <KeyPicker />
        <PianoDiagram
          highlights={litHighlights}
          octaves={2}
          startOctave={3}
          playable
          ariaLabel="Tonic chord tones — root, third, fifth"
        />
        <p className="mt-2 text-[12.5px] text-cyan-300/70">
          The L-H-M-H arpeggio walks the <strong>1</strong> →{" "}
          <strong>5</strong> → <strong>3</strong> → <strong>5</strong> of the
          tonic chord — gold (root) on the bottom, fifth and third on top.
        </p>
      </StudyDeck>

      <FadeIn>
        <Drill number="1" label="Where we are" title="Same song, deeper bass">
          <p>
            Lesson 3 had your left hand play single bass notes. This lesson
            keeps the right hand exactly the same — Twinkle's melody in{" "}
            <strong>{k.tonicName}</strong> — but turns the bass into a
            <em> flowing arpeggio</em> that fills every eighth note. Same
            melody; different motion.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "So the go-to arpeggio pattern I would suggest for you guys or for
            anyone, even myself included, would be this pattern."
          </PIQuote>
          <p>
            On his chart the pattern is <strong>L-H-M-H</strong> — low, high,
            middle, high.
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="2" label="The pattern" title="Low – High – Middle – High">
          <p>
            <strong>L-H-M-H</strong> means: low note, high note, middle note,
            high note — four eighth notes, so one cycle fills two beats and
            two cycles fill a bar.
          </p>
          <ArpeggioWave />
          <p>
            On the tonic chord in {k.tonicName} that's{" "}
            <strong>{SHAPES.T.map((o) => k.nameForPc((k.tonicPc + o + 12) % 12)).join(" – ")}</strong>.
            On the predominant chord (an inversion, so the low note stays put):{" "}
            <strong>{SHAPES.P.map((o) => k.nameForPc((k.tonicPc + o + 12) % 12)).join(" – ")}</strong>.
            On the dominant (a 7th chord):{" "}
            <strong>{SHAPES.D.map((o) => k.nameForPc((k.tonicPc + o + 12) % 12)).join(" – ")}</strong>.
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <PINoteImage
          src="/notes/piano-intermediate/finals/03-twinkle-twinkle-left-hand-bass-page2.png"
          caption="Jason's chart for the arpeggio pattern (L-H-M-H) on the tonic, predominant and dominant."
        />
      </FadeIn>

      <FadeIn>
        <Drill number="3" label="The chord plan" title="Three chords match the melody">
          <p>
            How do you know <em>which</em> chord goes under each half bar?
            Look at the melody note. The chord needs to contain a note of the
            melody, generally. Jason's chart (T = tonic, P = predominant,
            D = dominant), one chord per half bar:
          </p>
          <ul className="my-3 space-y-1.5 font-mono text-[14px] text-cyan-50/95">
            {CHORD_CHART.map((line, i) => (
              <li key={i}>Line {i + 1}: <strong>{line.join("  ")}</strong></li>
            ))}
          </ul>
          <p>
            "Little" sits on the 6th of the scale ({k.nameForPc(k.pcFromDegree(6))}),
            so the chord changes to the predominant, which contains it. Where
            the melody is on the 2nd, only the dominant contains it; on the
            4th, Jason uses the dominant in line 2 and the predominant in
            lines 3 and 4 — both contain it.
          </p>
          <PIInsight>
            <p>
              <strong>Pick chords by what's in the melody.</strong> Take three
              chords, look at the melody, and change chord when the melody
              note isn't in the one you're on. Jason uses Twinkle so you can
              take the same strategy to other songs.
            </p>
          </PIInsight>
        </Drill>
      </FadeIn>

      <FadeIn>
        <PINoteImage
          src="/notes/piano-intermediate/finals/03-twinkle-twinkle-left-hand-bass-page3.png"
          caption="Jason's chart — chords for Twinkle: T (tonic), P (predominant), D (dominant)."
        />
      </FadeIn>
      <FadeIn>
        <Drill number="4" label="The melody on the staff" title="Same melody as Lesson 3, transposes with the key">
          <Notation
            measures={melodyToMeasures(TWINKLE_MELODY, tonicMidi, 4, k.useFlats)}
            timeSig="4/4"
            width={1280}
            height={170}
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={async () => {
                kickAudio();
                await playSequence(
                  melodyToSequence(flatten(TWINKLE_MELODY), tonicMidi, 84, k.useFlats),
                  84
                );
              }}
              className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300"
            >
              ▶ Play melody alone in {k.tonicName}
            </button>
            <button
              onClick={playArpeggio}
              className="rounded-lg border border-amber-400/60 bg-amber-500/10 px-4 py-2 text-sm font-bold text-amber-200 hover:bg-amber-500/20"
            >
              ▶ Play LH arpeggio alone
            </button>
          </div>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="5" label="Generalising" title="1-3-5 / 1-4-6 / 7-4-5 — the universal arpeggio">
          <p>
            Once the L-H-H pattern is in your fingers, you stop thinking in
            note names. Every chord becomes a hand-shape:
          </p>
          <ul className="my-3 space-y-1.5 text-[14px] text-cyan-50/95">
            <li><strong>Tonic</strong> (I): scale degrees <strong>1 – 3 – 5</strong></li>
            <li><strong>Subdominant / predominant</strong> (IV): scale degrees <strong>1 – 4 – 6</strong></li>
            <li><strong>Dominant 7th</strong> (V7): scale degrees <strong>7 – 4 – 5</strong> (the 7 is the 3rd of V; the 4 is the 7th of V7)</li>
          </ul>
          <p>
            Transpose this shape mentally — not the notes — and you can play
            this pattern in any key. That's why the picker at the top of the
            page is here.
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="6" label="Play along" title="Melody + LH arpeggio together">
          <LessonMidi
            courseSlug="piano-intermediate"
            order="4"
            label={`Twinkle arpeggio · ${k.tonicName}`}
            originalBpm={84}
            onNote={onMidiNote}
          />
          {activePcs.size > 0 && (
            <PianoDiagram
              octaves={2}
              startOctave={3}
              compact
              highlights={[...activePcs].map((p): KeyHighlight => ({ pc: p, tone: "voice" }))}
            />
          )}
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="7" label="Practice ladder" title="Fifteen minutes, five rungs">
          <PracticeLadder
            intro={<>One pattern, four chord changes, then both hands. Hold any rung that wobbles.</>}
            rungs={[
              { title: "L-H-M-H on the tonic", minutes: 3, accent: "amber",   detail: `Eighth notes at 84. Just the tonic chord (${SHAPES.T.map((o) => k.nameForPc((k.tonicPc + o + 12) % 12)).join(" – ")}). Lock the cycle.` },
              { title: "Tonic → predominant", minutes: 3, accent: "cyan",    detail: "1-3-5 to 1-4-6: the low note stays on the 1; the 3 and 5 step up to the 4 and 6." },
              { title: "Tonic → dominant",    minutes: 3, accent: "rose",    detail: "1-3-5 to 7-4-5: the low note steps down to the 7, the 3 steps up to the 4 (the 7th of the V7 chord), the 5 stays." },
              { title: "Full chart alone",    minutes: 3, accent: "emerald", detail: "All six lines of Jason's chord chart, one L-H-M-H per half bar. No melody yet. Get the chord changes clean." },
              { title: "Both hands",        minutes: 3, accent: "violet",  detail: "Add the right-hand melody. If your hands lock to each other, drop back to LH alone." },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

/* —— Bespoke visual: animated L-H-H wave. Shows three vertical pulses
       (low, high, high) cycling at the lesson's tempo. Pure CSS animation. —— */
function ArpeggioWave() {
  const beats = ["L", "H", "M", "H", "L", "H", "M", "H"];
  return (
    <div className="my-4 rounded-2xl border border-cyan-400/30 bg-cyan-950/20 p-4">
      <div className="mb-2 flex items-baseline justify-between">
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-amber-300">
          ⤬ Pattern shape
        </div>
        <div className="font-mono text-[11px] text-cyan-300/80">
          ♪♪♪♪ ♪♪♪♪ &nbsp;·&nbsp; two cycles per bar
        </div>
      </div>
      <div className="flex h-24 items-end gap-2">
        {beats.map((kind, i) => {
          const isLow = kind === "L";
          const heightPct = isLow ? 30 : kind === "H" ? 95 : 65;
          const tone = isLow ? "bg-amber-400" : kind === "H" ? "bg-cyan-400" : "bg-emerald-400";
          return (
            <motion.div
              key={i}
              initial={{ scaleY: 0.2, opacity: 0.5 }}
              animate={{ scaleY: 1, opacity: 1 }}
              transition={{ delay: i * 0.12, duration: 0.35, ease: "easeOut" }}
              style={{ originY: 1, height: `${heightPct}%` }}
              className={`flex-1 rounded-t-md ${tone}`}
            >
              <div className="flex h-full items-start justify-center pt-1.5">
                <span className="font-mono text-[11px] font-bold text-zinc-900/85">
                  {kind}
                </span>
              </div>
            </motion.div>
          );
        })}
      </div>
      <div className="mt-2 grid grid-cols-8 gap-2 text-center text-[10px] uppercase tracking-wider text-cyan-300/70">
        <span>root</span><span>5th</span><span>3rd</span><span>5th</span>
        <span>root</span><span>5th</span><span>3rd</span><span>5th</span>
      </div>
    </div>
  );
}

/* ── shared ── */
function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3 shadow-[0_8px_30px_rgba(0,0,0,0.5)] backdrop-blur-md">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">✦ Study deck</div>
          <div className="text-[12px] text-cyan-300/70">Key picker · master keyboard · live</div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}

function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (
    <motion.div initial={reduced ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: "easeOut" }}>
      {children}
    </motion.div>
  );
}

function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote className="text-[17px] leading-relaxed text-cyan-50/95" style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}>
        {children}
      </blockquote>
      {attribution && <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">— {attribution}</figcaption>}
    </figure>
  );
}
